import React, { useMemo, type CSSProperties } from 'react';
// EAA-1821: the menu body, the open state and the outside-click/Escape wiring
// are no longer this file's — they are `FilterPopover` + `FilterOptionList` in
// `packages/ui`, which the TABLE HEADER's per-column funnel
// (`AuditColumnFilter`) renders too. A second copy of an option list is how the
// header filter and the bar filter drift into looking different.
import { Button, FilterOptionList, FilterPopover } from '@/components/ui';
import type { FilterPopoverTriggerState } from '@/components/ui';
import { ChevronDownIcon, FunnelIcon } from './icons';

/** The audit screen's own option shape — structurally the kit's
 *  `FilterOptionItem`, narrowed to string labels because every audit facet's
 *  copy comes from `actionLabels.ts` as plain text. */
export interface AuditFilterOption {
    value: string;
    label: string;
}

export interface AuditFilterChipProps {
    /** Facet name shown before the colon, e.g. "Hành động". Caller-supplied — no
     *  copy lives here. */
    label: string;
    /** Currently selected option values. */
    value: string[];
    options: AuditFilterOption[];
    onChange: (next: string[]) => void;
    /** Word shown as the value while nothing is selected, e.g. "Tất cả". */
    allLabel: string;
    /** Footer action inside the popover, e.g. "Bỏ chọn tất cả". */
    clearLabel: string;
    className?: string;
    style?: CSSProperties;
}

/** Past this many selected labels the chip would outgrow the bar, so it elides. */
const MAX_INLINE_VALUES = 2;

/**
 * EAA-1821: one multi-select facet chip for the Nhật ký filter bar.
 *
 * The chip is a READOUT as much as a trigger: unset it advertises how many
 * options exist ("Hành động: Tất cả · 7"), set it advertises what is chosen
 * and how many ("Hành động: Tạo · Sửa · 2") in brand ink. That is why the
 * badge changes meaning between the two states rather than always counting
 * one thing — an operator scanning the bar has to be able to tell a narrowed
 * facet from an untouched one without opening either.
 *
 * `open` state, dismissal and the option list itself now live in
 * `packages/ui`; what remains here is this chip's own trigger — the readout —
 * which the header's 26px icon box deliberately does NOT share.
 */
function AuditFilterChipBase({
    label,
    value,
    options,
    onChange,
    allLabel,
    clearLabel,
    className,
    style,
}: AuditFilterChipProps): React.ReactElement {
    const active = value.length > 0;

    // EAA-1821: read the labels off `options` rather than off the selection so
    // the chip renders them in the OPTIONS' order — otherwise the same two
    // filters would read "Tạo · Sửa" or "Sửa · Tạo" depending on click order.
    const valueText = useMemo(() => {
        if (!active) return allLabel;
        const selected = options
            .filter((option) => value.includes(option.value))
            .map((option) => option.label);
        if (selected.length <= MAX_INLINE_VALUES) return selected.join(' · ');
        return `${selected.slice(0, MAX_INLINE_VALUES).join(' · ')} …`;
    }, [active, allLabel, options, value]);

    const chipClassName = ['portal-audit-filter-chip', active ? 'is-active' : '', className ?? '']
        .filter(Boolean)
        .join(' ');

    const renderTrigger = ({ open, ref, toggle }: FilterPopoverTriggerState) => (
        <Button
            ref={ref}
            className={chipClassName}
            style={style}
            aria-haspopup="listbox"
            aria-expanded={open}
            onClick={toggle}
        >
            <FunnelIcon className="portal-audit-filter-chip__icon" />
            <span className="portal-audit-filter-chip__label">{label}:</span>
            <span className="portal-audit-filter-chip__value">{valueText}</span>
            <span className="portal-audit-filter-chip__badge">
                {active ? value.length : options.length}
            </span>
            <ChevronDownIcon className="portal-audit-filter-chip__icon" />
        </Button>
    );

    return (
        <FilterPopover className="portal-audit-filter-chip-root" trigger={renderTrigger}>
            <FilterOptionList
                className="portal-audit-filter-popover__body"
                label={label}
                options={options}
                value={value}
                onChange={onChange}
                clearLabel={clearLabel}
            />
        </FilterPopover>
    );
}

/**
 * EAA-1821: memoized because the bar sits directly above a paged audit table —
 * every keystroke in the search box and every page fetch re-renders the panel,
 * and without this each of those would re-render all four chips (and their
 * option lists) for a prop set that never changed. Callers must therefore pass
 * `useCallback`'d `onChange` and `useMemo`'d `options`/`value`.
 */
export const AuditFilterChip = React.memo(AuditFilterChipBase);
